import MakeModelChatType from '@/enums/MakeModelChatType';
import MakeModelChatQuestionType from '@/enums/MakeModelChatQuestionType';
import { store } from '@/store';
import SearchQuery from '@/models/SearchQuery';
import ExtraData from '@/models/ExtraData';
import { vehiclePaintCodeProviderService } from '@/services/VehiclePaintCodeProviderService';
import { imageUrlService } from '@/services/ImageUrlService';
import ChatMessage from './chatMessage';
import BodyStyleQuestionBuilder from './BodyStyleQuestionBuilder';
import PowerPreferenceQuestionBuilder from './PowerPreferenceQuestionBuilder';
import FuelTypeQuestionBuilder from './FuelTypeQuestionBuilder';
import TrimQuestionBuilder from './TrimQuestionBuilder';
import ColourQuestionBuilder from './ColourQuestionBuilder';
import QuestionBuilder from './QuestionBuilder';
import TransmissionQuestionBuilder from './TransmissionQuestionBuilder';

export default class ChatQuestion {
    public constructor(
        questionType: MakeModelChatQuestionType,
        question: string,
        questionInsight: string,
        answerResponses : Map<string, string>,
        helpText : string | null
    ) {
        const config = ChatQuestion.getQuestionConfig(questionType);

        this.makeModelChatQuestionType = questionType;
        this.question = question;
        this.questionInsight = questionInsight;
        this.bypassConfirmation = config.bypassConfirmation;
        this.includeImageInAnswerResponse = config.includeImageInAnswerResponse;
        this.answerResponses = answerResponses;
        this.helpText = helpText;
        this.answers = config.answers;
        this.setAnswer = config.setAnswer;
        this.isReady = config.isReady;
    }

    question: string = '';

    questionInsight: string = '';

    makeModelChatQuestionType: MakeModelChatQuestionType = MakeModelChatQuestionType.BodyStyle;

    bypassConfirmation: boolean;

    get showHelp() : boolean { 
        return this.helpText != null && this.helpText.length > 0;
    }

    isReady: boolean;

    canSkipQuestion: boolean = false;

    helpText: string | null;

    skipQuestion: () => Promise<boolean> = () => {
        const promise = new Promise<boolean>((resolve, reject) => {
                const iterator = setInterval(() => {
                    if (this.isReady) {
                        clearInterval(iterator);
                        resolve(this.canSkipQuestion);
                    }
                }, 100);
        });

        return promise;
    };

    answers: () => string[]; // the possible answers to the question

    includeImageInAnswerResponse : boolean;

    answerResponses : Map<string, string>;

    onAnswer(answer: string) : ChatMessage[] {
        this.setAnswer(answer);

        const responses : ChatMessage[] = [];

        const response = this.answerResponses.get(answer);
        if (response) {
            const chatMessage = new ChatMessage(MakeModelChatType.BotMessage);
            chatMessage.options.text = response;
            responses.push(chatMessage);
        }

        if (this.includeImageInAnswerResponse) {
            const imageChatMessage = new ChatMessage(MakeModelChatType.Image);
            imageChatMessage.options.imageURL = imageUrlService.imageUrl(undefined, undefined, undefined, undefined, this.currentBodyStyle!);
            responses.push(imageChatMessage);
        }

        return responses;
    } // the action to take when the user answers the question

    setAnswer: (answer: string) => void;

    answersReadyCallback(newVal: any, oldVal: any, chatMessages: ChatMessage[]) {
        const chatMessage = chatMessages.filter((x) => x.options.makeModelChatQuestionType === this.makeModelChatQuestionType)[0];
        if (chatMessage) {
            chatMessage.options.choices = newVal;
        }
    } // a callback to update the answers based on the user's previous answers

    onHelp() {
        let chatMessage = new ChatMessage(MakeModelChatType.BotMessage);
        if (this.helpText !== null) {
            chatMessage = new ChatMessage(MakeModelChatType.BotMessage, {
                text: this.helpText
            });
        }

        return chatMessage;
    }

    public get currentBodyStyle() : string | null {
        return this.searchQuery.bodystyles ? this.searchQuery.bodystyles[0] : null;
      }

    public get searchQuery(): SearchQuery {
    return store.getters['searchQueryModule/searchQuery'];
    }

    public get colour(): string {
        const extraData: ExtraData = store.getters['enquiryModule/extraData']
            .find((x: ExtraData) => x.keyName === 'basicColour');

        const manufacturerName = store.getters['vehicleModule/currentRange']?.manufacturerName;

        return extraData ? vehiclePaintCodeProviderService.GetPaintCode(extraData.keyValue, manufacturerName) : 'pspc0006';
    }

    public static getQuestionConfig(questionType: MakeModelChatQuestionType): QuestionBuilder {
        switch (questionType) {
            case MakeModelChatQuestionType.BodyStyle:
                return new BodyStyleQuestionBuilder();
            case MakeModelChatQuestionType.PowerPreference:
                return new PowerPreferenceQuestionBuilder();
            case MakeModelChatQuestionType.FuelType:
                return new FuelTypeQuestionBuilder();
            case MakeModelChatQuestionType.Trim:
                return new TrimQuestionBuilder();
            case MakeModelChatQuestionType.Colour:
                return new ColourQuestionBuilder();
            case MakeModelChatQuestionType.Transmission:
                return new TransmissionQuestionBuilder();
            default:
                throw new Error('Unknown Question Type');
        }
    }
}
